import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/components/layout/ThemeProvider';
import { scrollToSection as scrollTo } from '@/lib/scroll';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import cloudAILogo from '/src/images/cloudai-logo.svg';

const NAV_ITEMS = [
  { label: 'Home', section: 'hero' },
  { label: 'About', section: 'about' },
  { label: 'Courses', section: 'courses' },
  { label: 'Insights', section: 'insights' },
  { label: 'Contact', section: 'contact' },
];

const NAV_LINK_SPACING = 'px-4 py-2';
const HEADER_HEIGHT = 'h-[var(--header-height)]';
const HEADER_HEIGHT_SCROLLED = 'h-[var(--header-height-scrolled)]';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActive] = useState('hero');
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const ids = ['hero', 'about', 'courses', 'insights', 'contact', 'register'];
    const observers = ids.map((id) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActive(id);
        },
        { rootMargin: '-40% 0px -55% 0px' }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o?.disconnect());
  }, []);

  const handleNav = useCallback((section) => {
    scrollTo(section);
    setIsMenuOpen(false);
  }, []);

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  const isActive = (section) =>
    activeSection === section || (section === 'contact' && activeSection === 'register');

  const logoHeight = scrolled ? 'h-10 md:h-11' : 'h-11 md:h-12';

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled
          ? 'bg-background/95 backdrop-blur-md border-b border-border/60 shadow-[0_2px_20px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_20px_rgba(0,0,0,0.3)]'
          : 'bg-background/75 backdrop-blur-sm border-b border-transparent'
      )}
      role="banner"
    >
      <nav
        className="container mx-auto px-4 sm:px-6 lg:px-8"
        aria-label="Main navigation"
      >
        <div
          className={cn(
            'flex items-center justify-between transition-all duration-300',
            scrolled ? HEADER_HEIGHT_SCROLLED : HEADER_HEIGHT
          )}
        >
          <div className="flex shrink-0 items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleNav('hero')}
              className="h-auto w-auto p-0 rounded-lg hover:bg-transparent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label="CloudAI Leaders — back to top"
            >
              <img
                src={cloudAILogo}
                alt="CloudAI Leaders"
                className={cn(logoHeight, 'w-auto transition-all duration-300 object-contain')}
              />
            </Button>
          </div>

          <div className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map(({ label, section }) => (
              <Button
                key={section}
                variant="ghost"
                onClick={() => handleNav(section)}
                className={cn(
                  'nav-link text-nav font-semibold rounded-lg relative',
                  NAV_LINK_SPACING,
                  isActive(section)
                    ? 'text-primary bg-primary/10 hover:bg-primary/15'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/80'
                )}
              >
                {label}
                {isActive(section) && (
                  <span
                    className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                    style={{ background: 'linear-gradient(90deg, #0d9488, #f59e0b)' }}
                    aria-hidden
                  />
                )}
              </Button>
            ))}
            <div className="ml-2 w-px h-6 bg-border self-center" aria-hidden />
            <Button
              onClick={() => handleNav('register')}
              className={cn(
                'ml-3 rounded-full font-semibold text-sm',
                'bg-gradient-to-r from-teal-600 to-amber-500 hover:from-teal-700 hover:to-amber-600',
                'text-white shadow-md hover:shadow-lg transition-all duration-200',
                'px-5 py-2.5 h-auto'
              )}
            >
              Register Now
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="ml-2 h-9 w-9 rounded-full border-border"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="h-10 w-10 rounded-full border-border"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsMenuOpen((p) => !p)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              className="h-10 w-10 rounded-full border-border"
            >
              <AnimatePresence mode="wait" initial={false}>
                {isMenuOpen ? (
                  <motion.span
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <X className="h-5 w-5" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Menu className="h-5 w-5" />
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="md:hidden overflow-hidden border-t border-border"
            >
              <div className="py-4 space-y-1">
                {NAV_ITEMS.map(({ label, section }) => (
                  <Button
                    key={section}
                    variant="ghost"
                    onClick={() => handleNav(section)}
                    className={cn(
                      'w-full justify-start rounded-xl text-nav h-12 px-4',
                      isActive(section)
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    {label}
                  </Button>
                ))}
                <div className="pt-3 px-1">
                  <Button
                    onClick={() => handleNav('register')}
                    className="w-full rounded-full font-semibold h-11 bg-gradient-to-r from-teal-600 to-amber-500 hover:from-teal-700 hover:to-amber-600 text-white"
                  >
                    Register Now
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;
